import React, { useEffect, useRef, useState } from 'react'

/**
 * Renders children only when the container has non-zero dimensions.
 * Fixes dashboard charts sometimes rendering blank until refresh: Chart.js
 * can draw at 0x0 when the container is not yet laid out, and may not
 * redraw when the container later gets size. Mounting the chart only after
 * the container has dimensions ensures it always draws correctly.
 */
export const ChartContainer: React.FC<{
    children: React.ReactNode
    className?: string
    style?: React.CSSProperties
}> = ({ children, className, style }) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [hasSize, setHasSize] = useState(false)

    useEffect(() => {
        const el = containerRef.current
        if (!el) return

        const checkSize = () => {
            const { width, height } = el.getBoundingClientRect()
            setHasSize((prev) => {
                const now = width > 0 && height > 0
                return prev !== now ? now : prev
            })
        }

        checkSize()

        const observer = new ResizeObserver(checkSize)
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    return (
        <div
            ref={containerRef}
            className={className}
            style={{ minHeight: 140, width: '100%', ...style }}
        >
            {hasSize ? children : null}
        </div>
    )
}

export default ChartContainer
